/**
 * Every sitting of one paper by one student.
 *
 * The requirement sheet asks for each completed attempt to be kept and shown
 * with its score, percentage and date, so a student can see whether they are
 * improving rather than only how the last paper went.
 */

export interface AttemptRecord {
  attemptId: string;
  attemptNo: number;
  status: 'submitted' | 'expired';
  score: number;
  totalMarks: number;
  percentage: number;
  correctCount: number;
  incorrectCount: number;
  unansweredCount: number;
  timeUsedSeconds: number;
  submittedAt: string;
}

/** Most recent first, which is the order a student reads them in. */
export const newestFirst = (attempts: AttemptRecord[]): AttemptRecord[] =>
  [...(attempts ?? [])].sort((a, b) => b.attemptNo - a.attemptNo);

/** The best sitting, or nothing if there are none. */
export const bestAttempt = (attempts: AttemptRecord[]): AttemptRecord | undefined =>
  (attempts ?? []).reduce<AttemptRecord | undefined>(
    (best, attempt) => (!best || attempt.percentage > best.percentage ? attempt : best),
    undefined,
  );

/**
 * How this attempt compares with the one before it, which is the thing a
 * student actually wants to know from a list of scores.
 */
export const changeFromPrevious = (
  attempts: AttemptRecord[],
  attempt: AttemptRecord,
): number | undefined => {
  const previous = (attempts ?? []).find((entry) => entry.attemptNo === attempt.attemptNo - 1);

  if (!previous) {
    return undefined;
  }

  return Math.round((attempt.percentage - previous.percentage) * 100) / 100;
};

export const changeLabel = (change: number | undefined): string => {
  if (change === undefined || change === 0) {
    return '';
  }
  return change > 0 ? `+${change}%` : `${change}%`;
};

/** Why a paper closed, for the one column that needs it. */
export const closedLabel = (attempt: AttemptRecord): string =>
  attempt.status === 'expired' ? 'Time ran out' : 'Submitted';
